import { AddIcon, ChevronRightIcon, MoonIcon } from "@chakra-ui/icons";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	Button,
	Card,
	CardBody,
	Flex,
	Spinner,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Text,
	useDisclosure,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import {
	useEnterprise,
	useEnterpriseCenters,
} from "../hooks/enterprises.hooks";
import PageTitle from "../components/PageTitle";
import { useMemo } from "react";
import Address from "../components/Address";
import EnterpriseModal from "../components/modals/EnterpriseModal";
import PrimaryButton from "../components/PrimaryButton";
import { ColumnDef } from "@tanstack/react-table";
import { Center } from "../types/center.types";
import DataTable, {
	Cell,
	SearchBar,
} from "../components/table/ServerSideTable";
import PersistingTabIndex from "../components/PersistingTabIndex";
import { format } from "date-fns";
import SimpleModal from "../components/modals/SimpleModal";
import { useCentersPaginated } from "../hooks/centers.hooks";
import { linkCentersToEnterprise } from "../mutations/enterprise.mutations";
import { useMutation, useQueryClient } from "react-query";
import AcceptWithReasonModal, {
	ValidationEvent,
} from "../components/modals/AcceptWithReasonModal";
import BuildingIcon from "../components/icons/BuildingIcon";
import { Enterprise } from "../types/enterprises.types";
import { enterpriseToggleSleepMode } from "../mutations/enterprise.mutations";
import MoonSteroidIcon from "../components/icons/MoonSteroidIcon";
import { fPhoneNumber } from "../lib/format";

const EnterpriseBadge = ({ enterprise }: { enterprise: Enterprise }) => {
	return (
		<>
			<Flex
				w="100%"
				backgroundColor={"gray.100"}
				border="solid 1px"
				borderColor="gray.300"
				borderRadius={"xl"}
				my={4}
				py={2}
				justifyContent={"center"}
			>
				<Flex
					w="48px"
					h="48px"
					bg="brand.700"
					border="solid 1px"
					borderColor="white"
					justifyContent="center"
					alignItems="center"
					borderRadius={100}
					mr="8px"
				>
					<BuildingIcon w="24px" h="24px" color="white"></BuildingIcon>
				</Flex>
				<Flex direction={"column"} justifyContent={"center"}>
					<Text
						textTransform={"capitalize"}
						lineHeight="18px"
						fontSize="14px"
						fontWeight="600"
					>
						{enterprise.name}
					</Text>
					<Text
						lineHeight="15px"
						fontSize="10px"
						fontWeight="600"
						color="gray.500"
					>
						Entreprise
					</Text>
				</Flex>
			</Flex>
			<Flex
				w="100%"
				backgroundColor={"red.100"}
				border="solid 1px"
				borderColor="red.300"
				borderRadius={"xl"}
				my={4}
				py={2}
				justifyContent={"center"}
			>
				<Text
					lineHeight="18px"
					fontSize="14px"
					fontWeight="600"
					color="red.600"
				>
					Tous les centres liés seront mis en pause.
				</Text>
			</Flex>
		</>
	);
};

const EnterpriseCentersTable = ({ id }: { id: number }) => {
	const queryClient = useQueryClient();
	const { mutate: mutateLinkCenterToEnterprise } = useMutation(
		linkCentersToEnterprise,
		{
			onSuccess: () => {
				queryClient.invalidateQueries("centers");
				queryClient.invalidateQueries("enterprise-centers");
			},
		},
	);
	const columns = useMemo<ColumnDef<Center>[]>(
		() => [
			{
				accessorFn: (row) => row.id,
				id: "code",
				header: "Code",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => row.name,
				id: "name",
				header: "Nom du centre",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => fPhoneNumber(row.phoneNumber ?? ""),
				id: "phoneNumber",
				header: "Téléphone",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => <Address id={row.shippingAddressId}></Address>,
				id: "address",
				header: "Adresse",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => row.email,
				id: "email",
				header: "Email",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => <>{format(row.createdAt, "dd/MM/yyyy")}</>,
				id: "createdAt",
				header: "Date de création",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
		],
		[],
	);
	const EnterpriseCentersTableActions = () => {
		const addCentersModal = useDisclosure();
		const columns = useMemo<ColumnDef<Center>[]>(
			() => [
				{
					accessorFn: (row) => row.id,
					id: "code",
					header: "Code",
					cell: (info) => info.getValue(),
					sortDescFirst: true,
				},
				{
					accessorFn: (row) => row.name,
					id: "name",
					header: "Nom du centre",
					cell: (info) => info.getValue(),
					sortDescFirst: true,
				},
				{
					accessorFn: (row) => fPhoneNumber(row.phoneNumber ?? ""),
					id: "phoneNumber",
					header: "Téléphone",
					cell: (info) => info.getValue(),
					sortDescFirst: true,
				},
				{
					accessorFn: (row) => row.email,
					id: "email",
					header: "Email",
					cell: (info) => info.getValue(),
					sortDescFirst: true,
				},
			],
			[],
		);
		return (
			<>
				<PrimaryButton w={"fit-content"} onClick={addCentersModal.onOpen}>
					<AddIcon mr={4}></AddIcon>Ajouter un centre
				</PrimaryButton>
				<SimpleModal
					title="Ajouter un centre"
					isOpen={addCentersModal.isOpen}
					onClose={addCentersModal.onClose}
					w="60%"
					hideButtons
				>
					<DataTable
						columns={columns}
						paginatedQuery={useCentersPaginated}
						w={["10rem", "auto", "10rem", "20rem"]}
						flexGrow={[0, 1, 0, 0]}
						handleCellClick={(cell: Cell) => {
							mutateLinkCenterToEnterprise({
								enterprise: { id },
								centers: [cell.row.id],
							});
							addCentersModal.onClose();
						}}
					></DataTable>
				</SimpleModal>
			</>
		);
	};
	return (
		<DataTable
			columns={columns}
			paginatedQuery={useEnterpriseCenters({ id })}
			w={["8rem", "20rem", "10rem", "auto", "26rem", "10rem"]}
			flexGrow={[0, 0, 0, 1, 0, 0]}
			toolbar={
				<Flex my="1rem" justifyContent="space-between">
					<SearchBar />
					<EnterpriseCentersTableActions />
				</Flex>
			}
			cellLink={(cell: Cell) => {
				return `/contacts/centers/${cell.row.id}`;
			}}
		></DataTable>
	);
};

function ContactEnterprisesDetailPage() {
	const { id } = useParams();
	const { data: enterprise, isLoading } = useEnterprise(id);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const sleepModal = useDisclosure();
	const queryClient = useQueryClient();
	const { mutate: mutateEnterSleepMode } = useMutation(
		enterpriseToggleSleepMode,
		{
			onSuccess: () => {
				queryClient.invalidateQueries("enterprises");
			},
		},
	);
	const handleSleepMode = (validation: ValidationEvent) => {
		mutateEnterSleepMode({
			id,
			sleep: enterprise?.sleepModeAt === null,
			reason: validation.data.reason,
		});
	};

	if (!enterprise || isLoading) {
		return <Spinner></Spinner>;
	}
	return (
		<>
			<Breadcrumb
				spacing="8px"
				separator={<ChevronRightIcon color="gray.500" />}
			>
				<BreadcrumbItem>
					<BreadcrumbLink
						href="/contacts"
						color="gray.600"
						fontWeight={600}
						fontSize="14px"
					>
						Contact
					</BreadcrumbLink>
				</BreadcrumbItem>

				<BreadcrumbItem>
					<BreadcrumbLink
						href="#"
						color="brand.700"
						fontWeight={600}
						fontSize="14px"
					>
						<Text textTransform={"capitalize"}>{enterprise.name}</Text>
					</BreadcrumbLink>
				</BreadcrumbItem>
			</Breadcrumb>

			<Flex justifyContent="space-between">
				<PageTitle>
					<Text textTransform={"capitalize"}>{enterprise.name}</Text>
				</PageTitle>

				{enterprise.sleepModeAt !== null && (
					<Flex
						backgroundColor={"brand.50"}
						direction={"column"}
						alignItems={"left"}
						p={4}
						borderRadius="xl"
						border="solid 1px"
						borderColor="gray.300"
						w={"400px"}
					>
						<MoonSteroidIcon h={"38px"} w={"38px"} color={"brand.700"} />

						<Flex direction={"column"} mt={1}>
							<Text
								lineHeight="21px"
								fontSize="14px"
								fontWeight="600"
								color="gray.700"
							>
								Entreprise en sommeil
							</Text>
							<Text
								lineHeight="21px"
								fontSize="14px"
								fontWeight="400"
								color="gray.600"
							>
								{enterprise.sleepModeReason}
							</Text>
						</Flex>

						<Button
							color={"brand.700"}
							variant="link"
							w="fit-content"
							mt={4}
							onClick={() =>
								mutateEnterSleepMode({
									id,
									sleep: false,
									reason: "",
								})
							}
						>
							Réactiver
						</Button>
					</Flex>
				)}
			</Flex>

			<Flex gap={4} mt="2rem">
				<Flex direction={"column"} w={"10rem"}>
					<Text
						lineHeight="15px"
						fontSize="10px"
						fontWeight="700"
						color="gray.400"
					>
						Commandes
					</Text>
					<Text
						lineHeight="36px"
						fontSize="24px"
						fontWeight="600"
						color="gray.950"
					>
						{enterprise.ordersCount}
					</Text>
				</Flex>

				<Flex direction={"column"} w={"10rem"}>
					<Text
						lineHeight="15px"
						fontSize="10px"
						fontWeight="700"
						color="gray.400"
					>
						Utilisateurs
					</Text>
					<Text
						lineHeight="36px"
						fontSize="24px"
						fontWeight="600"
						color="gray.950"
					>
						{enterprise.usersCount}
					</Text>
				</Flex>
			</Flex>

			<PersistingTabIndex id="contact-enterprises" mt={6}>
				<TabList>
					<Tab _selected={{ color: "brand.700", borderColor: "brand.700" }}>
						Informations
					</Tab>
					<Tab _selected={{ color: "brand.700", borderColor: "brand.700" }}>
						Centres
					</Tab>
				</TabList>

				<TabPanels>
					<TabPanel px={0}>
						<Card mt={4}>
							<CardBody>
								<Flex
									my="12px"
									mx="4px"
									py="12px"
									borderBottom="1px"
									borderColor="gray.100"
									justifyContent={"space-between"}
									_hover={{ bg: "zinc.50" }}
								>
									<Text>Nom</Text>
									<Text textTransform={"capitalize"}>{enterprise.name}</Text>
								</Flex>

								<Flex
									my="12px"
									mx="4px"
									py="12px"
									borderBottom="1px"
									borderColor="gray.100"
									justifyContent={"space-between"}
									_hover={{ bg: "zinc.50" }}
								>
									<Text>ID sage</Text>
									<Text>{enterprise.sageId}</Text>
								</Flex>

								<Flex
									my="12px"
									mx="4px"
									py="12px"
									borderBottom="1px"
									borderColor="gray.100"
									justifyContent={"space-between"}
									_hover={{ bg: "zinc.50" }}
								>
									<Text>Adresse de facturation</Text>
									<Address id={enterprise.addressId}></Address>
								</Flex>

								<Flex
									my="12px"
									mx="4px"
									py="12px"
									borderBottom="1px"
									borderColor="gray.100"
									justifyContent={"space-between"}
									_hover={{ bg: "zinc.50" }}
								>
									<Text>Téléphone</Text>
									<Text>{fPhoneNumber(enterprise.phoneNumber ?? "")}</Text>
								</Flex>

								<Flex
									my="12px"
									mx="4px"
									py="12px"
									justifyContent={"space-between"}
									_hover={{ bg: "zinc.50" }}
								>
									<Text>Email de facturation</Text>
									<Text>{enterprise.email}</Text>
								</Flex>

								<Flex w="100%" justifyContent={"end"}>
									<Button onClick={onOpen}>Modifier</Button>
									<EnterpriseModal
										id={enterprise.id}
										isOpen={isOpen}
										onOpen={onOpen}
										onClose={onClose}
									></EnterpriseModal>
								</Flex>

								{enterprise.sleepModeAt === null && (
									<Flex mt={4}>
										<Button onClick={sleepModal.onOpen}>Mise en sommeil</Button>
										<AcceptWithReasonModal
											title={"Mise en sommeil"}
											icon={<MoonIcon color="brand.700" />}
											description={<EnterpriseBadge enterprise={enterprise} />}
											discardButtonText="Annuler"
											validationButtonText="Mettre en sommeil"
											colorScheme="brand"
											isOpen={sleepModal.isOpen}
											onClose={sleepModal.onClose}
											onValidation={handleSleepMode}
										></AcceptWithReasonModal>
									</Flex>
								)}
							</CardBody>
						</Card>
					</TabPanel>
					<TabPanel px={0}>
						<EnterpriseCentersTable id={enterprise.id}></EnterpriseCentersTable>
					</TabPanel>
				</TabPanels>
			</PersistingTabIndex>
		</>
	);
}

export default ContactEnterprisesDetailPage;
